import React, { useState } from "react";
import { Lock, X, CheckCircle, AlertCircle, Loader2, Info } from "lucide-react";

/**
 * Mark as Closed Modal Component
 * Allows users to mark an event as closed
 */
const MarkAsClosedModal = ({
  isOpen,
  onClose,
  onConfirm,
  eventName,
  isLoading = false,
}) => {
  const [confirmText, setConfirmText] = useState("");
  const requiredText = "CLOSE";

  if (!isOpen) return null;

  const isConfirmed = confirmText.trim().toUpperCase() === requiredText;
  const handleClose = () => {
    if (!isLoading) {
      setConfirmText("");
      onClose();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isConfirmed) {
      onConfirm();
    }
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all">
        {/* Header */}
        <div className="flex items-start mb-6 space-x-4">
          <div className="flex-shrink-0 p-3 bg-purple-100 rounded-full">
            <Lock className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="mb-1 text-xl font-bold text-gray-900">
              Mark Event as Closed
            </h3>
            <p className="text-sm text-gray-600">
              Close{" "}
              <span className="font-semibold text-gray-900">
                {eventName || "this event"}
              </span>{" "}
              to prevent new ticket sales
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info Message */}
        <div className="p-4 mb-6 bg-purple-50 rounded-xl border border-purple-100">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-800">
              <p className="mb-2 font-medium">What happens when you close an event:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>No new tickets can be sold</li>
                <li>Existing ticket holders retain access</li>
                <li>Event remains visible but marked as closed</li>
                <li>You can reopen the event later if needed</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Confirmation Input */}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Type <span className="font-mono text-purple-600">{requiredText}</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={requiredText}
              className={`w-full px-4 py-3 text-sm font-mono text-center border rounded-xl transition-all focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                isConfirmed
                  ? "border-green-300 bg-green-50"
                  : "border-gray-300 bg-white"
              }`}
              disabled={isLoading}
              autoFocus
            />
            {isConfirmed && (
              <div className="flex items-center justify-center mt-2 space-x-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-xs font-medium">Confirmed</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !isConfirmed}
              className="flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white bg-purple-600 rounded-xl transition-colors hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Closing...
                </>
              ) : (
                <>
                  <Lock className="mr-2 w-4 h-4" />
                  Mark as Closed
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarkAsClosedModal;

